import apiClient from './api-client';

// ============================================================================
// API PATH CONSTANTS
// ============================================================================

const API_BASE = '/admin/data-principals';
const API_PATHS = {
    BASE: API_BASE,
    BY_ID: (id: number) => `${API_BASE}/${id}`,
    STATS: `${API_BASE}/stats`,
    RIGHTS_REQUESTS: (id: number) => `${API_BASE}/${id}/rights-requests`,
} as const;

// ============================================================================
// INTERFACES
// ============================================================================

export interface DataPrincipalFilters {
    page?: number;
    pageSize?: number;
    identifierType?: 'EMAIL' | 'PHONE';
    consentStatus?: 'ACTIVE' | 'INACTIVE' | 'WITHDRAWN' | 'EXPIRED' | 'UNKNOWN';
    isTest?: boolean;
    isActive?: boolean;
    startDate?: string; // ISO date string
    endDate?: string; // ISO date string
    dateField?: 'CreatedDate' | 'LastActivityDate' | 'LastConsentDate';
    search?: string;
    searchType?: 'DPID' | 'TOKEN' | 'EXTERNAL_ID' | 'EMAIL' | 'PHONE';
}

export interface DataPrincipalListItem {
    dpId: number;
    clientId: number;
    identifierType: string;
    identifierHash: string;
    identifierValue: string | null; // Only for test data
    externalIdentifier: string | null;
    vaultId: number | null;
    formatPreservingToken: string | null; // Masked token for display when vaultId exists
    overallConsentStatus: string;
    lastConsentDate: string | null; // ISO date string
    lastConsentWithdrawalDate: string | null; // ISO date string
    lastActivityDate: string | null; // ISO date string
    createdDate: string; // ISO date string
    isActive: boolean;
    isTest: boolean;
    isMinor: boolean;
    hasRequestedDataAccess: boolean;
    hasRequestedDataCorrection: boolean;
    hasRequestedDataErasure: boolean;
    hasRequestedDataPortability: boolean;
    hasWithdrawnConsent: boolean;
}

export interface DataPrincipalsListData {
    dataPrincipals: DataPrincipalListItem[];
    totalCount: number;
    totalPages: number;
    currentPage: number;
    pageSize: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
}

export interface DataPrincipalsListResponse {
    success: boolean;
    data: DataPrincipalsListData;
    error?: string;
    errorType?: string;
}

export interface DataPrincipalDetails extends DataPrincipalListItem {
    consentManagementEnabled: boolean;
    lastConsentManagementAccess: string | null; // ISO date string
    dataCategoriesCollected: string | null; // JSON string
    dataProcessingPurposes: string | null; // JSON string
    preferredRetentionPeriod: number | null;
    retentionExtensionAllowed: boolean;
    dataDeletionRequested: boolean;
    dataDeletionRequestedDate: string | null; // ISO date string
    dataSubjectRightsAcknowledged: boolean;
    privacyPolicyAccepted: boolean;
    privacyPolicyAcceptedDate: string | null; // ISO date string
    termsOfServiceAccepted: boolean;
    termsOfServiceAcceptedDate: string | null; // ISO date string
    // Parent/Guardian information (for minor DPs)
    parentIdentifierType: string | null;
    parentIdentifierValue: string | null; // Only for test data
    parentVaultId: number | null;
    parentFormatPreservingToken: string | null; // Masked token for display when parentVaultId exists
}

export interface DataPrincipalDetailsResponse {
    success: boolean;
    data: DataPrincipalDetails;
    error?: string;
    errorType?: string;
}

export interface DataPrincipalStats {
    total: number;
    active: number; // IsActive = 1
    test: number; // IsTest = 1
    production: number; // IsTest = 0
    activeConsent: number; // OverallConsentStatus = 'ACTIVE'
    withdrawnConsent: number; // OverallConsentStatus = 'WITHDRAWN'
    expiredConsent: number; // OverallConsentStatus = 'EXPIRED'
    unknownConsent: number; // OverallConsentStatus = 'UNKNOWN'
    email: number; // IdentifierType = 'EMAIL'
    phone: number; // IdentifierType = 'PHONE'
}

export interface DataPrincipalStatsResponse {
    success: boolean;
    data: DataPrincipalStats;
    error?: string;
    errorType?: string;
}

export interface RightsRequestItem {
    requestId: number;
    status: string;
    createdDate: string; // ISO date string
}

export interface DPRightsRequestsSummary {
    dataAccess: { count: number; requests: RightsRequestItem[] };
    dataCorrection: { count: number; requests: RightsRequestItem[] };
    dataErasure: { count: number; requests: RightsRequestItem[] };
    dataPortability: { count: number; requests: RightsRequestItem[] };
    consentWithdrawal: { count: number; requests: RightsRequestItem[] };
    totalOpen: number;
}

export interface DPRightsRequestsSummaryResponse {
    success: boolean;
    data: DPRightsRequestsSummary;
    error?: string;
    errorType?: string;
}

export interface ConsentHistoryItem {
    consentHeaderId: number;
    consentDetailId: number;
    consentDate: string; // ISO date string
    consentValue: boolean;
    purposeId: number;
    purposeName: string;
    purposeDescription: string | null;
    collectionPointId: number;
    collectionPointName: string;
    collectionPointIdentifier: string;
    collectionPointType: string;
    retentionExpirationDate: string | null; // ISO date string
    isTest: boolean;
    ipAddress: string | null;
    userAgent: string | null;
    languageCode: string | null;
    countryCode: string | null;
    region: string | null;
    city: string | null;
    /** Source of consent: NOTICE, NOTICE_OTP, WEB, API, etc. */
    consentSource?: string | null;
}

export interface ConsentHistoryResponse {
    success: boolean;
    data: ConsentHistoryItem[];
    error?: string;
    errorType?: string;
}

export interface ConsentBreakdownItem {
    purposeId: number;
    purposeName: string;
    purposeDescription: string | null;
    lawfulGround: string;
    lawfulGroundCode: string;
    consentStatus: 'ACTIVE' | 'INACTIVE' | 'WITHDRAWN' | 'EXPIRED' | 'NO_CONSENT';
    consentValue: boolean | null;
    lastConsentDate: string | null; // ISO date string
    retentionExpirationDate: string | null; // ISO date string
    collectionPoints: Array<{
        collectionPointId: number;
        collectionPointName: string;
        collectionPointIdentifier: string;
        collectionPointType: string;
    }>;
}

export interface ConsentBreakdownResponse {
    success: boolean;
    data: ConsentBreakdownItem[];
    error?: string;
    errorType?: string;
}

export interface DataElementValue {
    consentDataElementId: number;
    consentHeaderId: number;
    dataElementValue: string | null; // Direct value (for test data)
    vaultId: number | null; // Vault ID (for production data)
    storageMode: 'VAULT' | 'DIRECT';
    isPersonalData: boolean;
    isTest: boolean;
    collectionPointId: number;
    collectionPointName: string;
    collectionPointIdentifier: string;
    collectedDate: string; // ISO date string
    formatPreservingToken: string | null; // For display when vaultId exists
    partiallyMaskedToken: string | null; // For display when vaultId exists
}

export interface DataElementItem {
    dataElementId: number;
    dataElementName: string;
    dataElementDescription: string | null;
    dataCategory: string | null;
    values: DataElementValue[]; // Actual values collected
}

export interface DataElementsResponse {
    success: boolean;
    data: DataElementItem[];
    error?: string;
    errorType?: string;
}

export interface CreateDataPrincipalRequest {
    identifier: string;
    identifierType: 'EMAIL' | 'PHONE';
    externalIdentifier?: string | null;
    defaultLanguageCode?: string | null;
    isTest: boolean;
    isMinor?: boolean;
    parentIdentifier?: string | null;
    parentIdentifierType?: 'EMAIL' | 'PHONE' | null;
}

export interface CreateDataPrincipalResponse {
    success: boolean;
    data?: DataPrincipalDetails;
    error?: string;
    errorType?: string;
    existingDP?: {
        dpId: number;
        identifierDisplay: string;
        externalIdentifier: string | null;
        identifierType: string;
    };
}

// ============================================================================
// SERVICE METHODS
// ============================================================================

export const dataPrincipalsService = {
    /**
     * List data principals with filtering and pagination
     */
    listDataPrincipals: async (filters: DataPrincipalFilters): Promise<DataPrincipalsListData> => {
        try {
            const params = new URLSearchParams();
            
            if (filters.page !== undefined) {
                params.append('page', filters.page.toString());
            }
            if (filters.pageSize !== undefined) {
                params.append('pageSize', filters.pageSize.toString());
            }
            if (filters.identifierType) {
                params.append('identifierType', filters.identifierType);
            }
            if (filters.consentStatus) {
                params.append('consentStatus', filters.consentStatus);
            }
            if (filters.isTest !== undefined) {
                params.append('isTest', filters.isTest.toString());
            }
            if (filters.isActive !== undefined) {
                params.append('isActive', filters.isActive.toString());
            }
            if (filters.startDate) {
                params.append('startDate', filters.startDate);
            }
            if (filters.endDate) {
                params.append('endDate', filters.endDate);
            }
            if (filters.dateField) {
                params.append('dateField', filters.dateField);
            }
            if (filters.search) {
                params.append('search', filters.search);
            }
            if (filters.searchType) {
                params.append('searchType', filters.searchType);
            }

            const response = await apiClient.get<DataPrincipalsListResponse>(
                `${API_PATHS.BASE}?${params.toString()}`
            );

            if (!response.success || !response.data) {
                throw new Error(response.error || 'Failed to fetch data principals');
            }

            return response.data;
        } catch (error) {
            console.error('Error listing data principals:', error);
            throw error;
        }
    },

    /**
     * Get data principal by ID
     */
    getDataPrincipal: async (dpId: number): Promise<DataPrincipalDetails> => {
        try {
            const response = await apiClient.get<DataPrincipalDetailsResponse>(
                API_PATHS.BY_ID(dpId)
            );

            if (!response.success || !response.data) {
                throw new Error(response.error || 'Failed to fetch data principal');
            }

            return response.data;
        } catch (error) {
            console.error('Error getting data principal:', error);
            throw error;
        }
    },

    /**
     * Get data principal statistics
     */
    getDataPrincipalStats: async (): Promise<DataPrincipalStats> => {
        try {
            const response = await apiClient.get<DataPrincipalStatsResponse>(
                API_PATHS.STATS
            );

            if (!response.success || !response.data) {
                throw new Error(response.error || 'Failed to fetch data principal statistics');
            }

            return response.data;
        } catch (error) {
            console.error('Error getting data principal stats:', error);
            throw error;
        }
    },

    /**
     * Get rights requests summary for a data principal
     */
    getDPRightsRequestsSummary: async (dpId: number): Promise<DPRightsRequestsSummary> => {
        try {
            const response = await apiClient.get<DPRightsRequestsSummaryResponse>(
                API_PATHS.RIGHTS_REQUESTS(dpId)
            );

            if (!response.success || !response.data) {
                throw new Error(response.error || 'Failed to fetch rights requests summary');
            }

            return response.data;
        } catch (error) {
            console.error('Error getting DP rights requests summary:', error);
            throw error;
        }
    },

    /**
     * Get consent history for a data principal
     */
    getDPConsentHistory: async (dpId: number): Promise<ConsentHistoryItem[]> => {
        try {
            const response = await apiClient.get<ConsentHistoryResponse>(
                `${API_PATHS.BY_ID(dpId)}/consent-history`
            );

            if (!response.success || !response.data) {
                throw new Error(response.error || 'Failed to fetch consent history');
            }

            return response.data;
        } catch (error) {
            console.error('Error getting DP consent history:', error);
            throw error;
        }
    },

    /**
     * Get consent breakdown by purpose for a data principal
     */
    getDPConsentBreakdown: async (dpId: number): Promise<ConsentBreakdownItem[]> => {
        try {
            const response = await apiClient.get<ConsentBreakdownResponse>(
                `${API_PATHS.BY_ID(dpId)}/consent-breakdown`
            );

            if (!response.success || !response.data) {
                throw new Error(response.error || 'Failed to fetch consent breakdown');
            }

            return response.data;
        } catch (error) {
            console.error('Error getting DP consent breakdown:', error);
            throw error;
        }
    },

    /**
     * Get data elements collected for a data principal
     */
    getDPDataElements: async (dpId: number): Promise<DataElementItem[]> => {
        try {
            const response = await apiClient.get<DataElementsResponse>(
                `${API_PATHS.BY_ID(dpId)}/data-elements`
            );

            if (!response.success || !response.data) {
                throw new Error(response.error || 'Failed to fetch data elements');
            }

            return response.data;
        } catch (error) {
            console.error('Error getting DP data elements:', error);
            throw error;
        }
    },

    /**
     * Create a new data principal
     */
    createDataPrincipal: async (request: CreateDataPrincipalRequest): Promise<DataPrincipalDetails> => {
        try {
            const response = await apiClient.post<CreateDataPrincipalResponse>(
                API_PATHS.BASE,
                request
            );

            if (!response.success || !response.data) {
                throw new Error(response.error || 'Failed to create data principal');
            }

            return response.data;
        } catch (error) {
            console.error('Error creating data principal:', error);
            throw error;
        }
    },
};

export default dataPrincipalsService;

